import type { FirebaseApp } from 'firebase/app';
import type { Firestore } from 'firebase/firestore';
import {
  doc, getFirestore, setDoc, Timestamp,
} from 'firebase/firestore';

import { DEBUG } from '../config';
import { ACCOUNT_SLOT } from './multiaccount';

// Глобальные константы, определяемые webpack DefinePlugin
declare const APP_VERSION: string;

let db: Firestore | undefined;
let customUserId: string | undefined;
let customKeys: Record<string, any> = {};

/**
 * Инициализация Crashlytics (Firestore)
 * Вызывается при инициализации Firebase
 */
export function initializeCrashlytics(firebaseApp: FirebaseApp): void {
  try {
    db = getFirestore(firebaseApp);
    // eslint-disable-next-line no-console
    console.log('[Crashlytics] Initialized successfully');
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[Crashlytics] Firestore not available', error);
  }
}

interface CrashLogData {
  component?: string;
  url: string;
  error: {
    name: string;
    message: string;
    stack?: string;
  };
  userAgent: string;
  timestamp: Timestamp;
  userId?: string;
  sessionId?: string;
  appVersion?: string;
  environment: string;
  customKeys?: Record<string, any>;
  extraInfo?: Record<string, any>;
}

/**
 * Логирование fatal crash
 * @param error - объект Error
 * @param context - дополнительный контекст
 */
export async function logCrash(
  error: Error,
  context?: {
    component?: string;
    extraInfo?: Record<string, any>;
  },
): Promise<void> {
  // В режиме разработки только console.log (если не форсируем отправку)
  if (DEBUG && !context?.extraInfo?.forceSend) {
    // eslint-disable-next-line no-console
    console.log('[Crashlytics] Would send crash:', {
      error: error.message,
      stack: error.stack,
      context,
    });
    return;
  }

  if (!db) {
    // eslint-disable-next-line no-console
    console.warn('[Crashlytics] Not initialized');
    return;
  }

  try {
    const logName = new Date().toISOString().replace(/:/g, '-').replace(/\./g, '-');

    const logData: CrashLogData = {
      component: context?.component || 'Unknown',
      url: window.location.href,
      error: {
        name: error.name || 'Error',
        message: error.message || String(error),
        stack: error.stack,
      },
      userAgent: navigator.userAgent,
      timestamp: Timestamp.now(),
      userId: customUserId,
      sessionId: getSessionId(),
      appVersion: getAppVersion(),
      environment: DEBUG ? 'development' : 'production',
      customKeys: Object.keys(customKeys).length > 0 ? { ...customKeys } : undefined,
      extraInfo: context?.extraInfo,
    };

    await setDoc(doc(db, 'ErrorLogs', logName), logData);
    // eslint-disable-next-line no-console
    console.log('[Crashlytics] Crash logged:', logName);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[Crashlytics] Failed to send crash log:', err);
  }
}

/**
 * Логирование non-fatal ошибки
 * @param message - сообщение об ошибке
 * @param context - дополнительный контекст (может содержать component и другие поля)
 */
export async function logNonFatalError(
  message: string,
  context?: {
    component?: string;
    [key: string]: any;
  },
): Promise<void> {
  const error = new Error(message);
  error.name = 'NonFatalError';

  // Извлекаем component из context, если он есть
  const { component = 'NonFatal', ...extraInfo } = context || {};

  await logCrash(error, {
    component,
    extraInfo: { ...extraInfo, fatal: false },
  });
}

/**
 * Установка user ID для отслеживания ошибок
 * @param userId - ID пользователя
 */
export function setUserId(userId: string | undefined): void {
  const isNewUser = userId && userId !== customUserId;
  customUserId = userId;

  // Генерировать новый session ID при смене пользователя (не при logout)
  if (isNewUser) {
    const storageKey = getSessionStorageKey();
    const newSessionId = generateNewSessionId();
    sessionStorage.setItem(storageKey, newSessionId);
    // eslint-disable-next-line no-console
    console.log('[Crashlytics] New session started for user:', userId);
  }

  // eslint-disable-next-line no-console
  console.log('[Crashlytics] User ID set:', userId);
}

/**
 * Добавление custom key-value для контекста
 * @param key - ключ
 * @param value - значение
 */
export function setCustomKey(key: string, value: any): void {
  customKeys[key] = value;
}

/**
 * Очистка всех custom keys
 */
export function clearCustomKeys(): void {
  customKeys = {};
}

/**
 * Генерация нового session ID
 */
function generateNewSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
}

/**
 * Получение ключа для sessionStorage с учетом мультиаккаунтов
 */
function getSessionStorageKey(): string {
  const accountSlot = ACCOUNT_SLOT || 1;
  return `crashlytics_session_id_${accountSlot}`;
}

/**
 * Получение session ID (генерируется при загрузке страницы или при смене пользователя)
 */
function getSessionId(): string {
  const storageKey = getSessionStorageKey();
  let sessionId = sessionStorage.getItem(storageKey);
  if (!sessionId) {
    sessionId = generateNewSessionId();
    sessionStorage.setItem(storageKey, sessionId);
  }
  return sessionId;
}

/**
 * Очистка session ID (вызывается при logout)
 */
export function clearSession(): void {
  const storageKey = getSessionStorageKey();
  sessionStorage.removeItem(storageKey);
}

/**
 * Получение версии приложения
 */
function getAppVersion(): string {
  try {
    // APP_VERSION определяется webpack DefinePlugin во время сборки
    return typeof APP_VERSION !== 'undefined' ? APP_VERSION : 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Экспорт для тестирования
 */
export function isInitialized(): boolean {
  return db !== undefined;
}
